'use client';

import { Check } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import type { Source } from '@/types/nestjs';
import { useDeveloperFilterStore } from '../Store/store';
import { findSelectedSource } from '../utils/utils';

interface ContentProps {
  developerSources: Source[];
}

const Content = ({ developerSources }: ContentProps) => {
  const { developerId, setDeveloperId } = useDeveloperFilterStore();
  const [searchValue, setSearchValue] = useState('');
  const listRef = useRef<HTMLDivElement | null>(null);

  const filteredSources = developerSources.filter((s) => s.name.toLowerCase().includes(searchValue.toLowerCase()));

  const selectedSource = findSelectedSource(developerSources, developerId);

  const handleOnSelect = (value: string) => {
    setDeveloperId({
      developerId: value === 'all' ? null : Number(value),
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: searchValue is an intentional trigger, not read in the body — the effect resets the list's scrollTop to 0 whenever the search query changes. Removing it (as suggested) would run this only on mount and break scroll-reset-on-filter.
  useEffect(() => {
    const currentListRef = listRef.current;
    if (!currentListRef) {
      return;
    }
    currentListRef.scrollTop = 0;
  }, [searchValue]);

  return (
    <Command className='relative w-full overflow-visible'>
      <CommandInput
        placeholder='Sök bostadsutvecklare...'
        value={searchValue}
        onValueChange={setSearchValue}
      />
      <CommandEmpty className='text-foreground font-normal'>Hittade ingen bostadsutvecklare.</CommandEmpty>
      <CommandList
        ref={listRef}
        className='scrollbar laptop:max-w-none laptop:w-(--radix-popover-trigger-width) w-full max-w-60 min-w-60
          rounded-md shadow-2xl'
      >
        <CommandGroup>
          {filteredSources.map((developerSource) => (
            <CommandItem
              key={developerSource.id}
              value={developerSource.id.toString()}
              keywords={[developerSource.name]}
              onSelect={handleOnSelect}
              data-command-item
            >
              {developerSource.name}
              <Check
                className={`ml-auto h-4 w-4 ${developerSource.id === selectedSource?.id ? 'opacity-100' : 'opacity-0'}`}
              />
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};

export default Content;
